<template>
  <span class="text-s font-regular text-grey-body">
    <span v-for="(part, index) in messageParts" :key="index">
      <template v-if="part.type === 'text'">{{ part.value }}</template>
      <router-link
        v-else
        class="font-medium underline text-grey-clickable focus-visible:outline-2 focus-visible:outline-dotted focus-visible:outline-primary"
        :to="part.href"
        >{{ part.label }}</router-link
      >
    </span>
  </span>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import type { SeekerAdminMessageData, RequestKind } from '@/apps/messages/helpers/adminMessageFormatter';

type MessagePart = { type: 'text'; value: string } | { type: 'link'; href: string; label: string };

const requestLabel = (kind: RequestKind): string => (kind === 'booking' ? 'booking request' : 'interview request');

function buildMessageParts(data: SeekerAdminMessageData): MessagePart[] {
  const parts: MessagePart[] = [];
  const text = (value: string) => parts.push({ type: 'text', value });
  const link = (href: string, label: string) => parts.push({ type: 'link', href, label });

  switch (data.type) {
    case 'interview_request_created':
      text('You created a ');
      link(`/requests/${data.interviewId}`, requestLabel(data.kind));
      break;
    case 'interview_contract_accepted':
      text(`${data.otherUserName} accepted your `);
      link(`/bookings/${data.contractId}`, requestLabel(data.kind));
      break;
    case 'job_application_replied_parent':
      text(`You replied to ${data.otherUserName}'s `);
      if (data.jobUuid) {
        link(`/jobs/${data.jobUuid}/applications/${data.jobAppUuid}`, 'job application');
      } else {
        text('job application');
      }
      break;
    case 'interview_declined':
      text(
        data.kind === 'booking'
          ? `${data.otherUserName} declined your booking request`
          : `${data.otherUserName} declined your interview request`
      );
      break;
    case 'interview_canceled':
      text(data.kind === 'booking' ? 'You cancelled the booking request' : 'You cancelled the interview request');
      break;
    case 'parent_contract_cancelation':
      text(data.kind === 'booking' ? 'You cancelled the booking' : 'You cancelled the interview');
      break;
    case 'sitter_contract_cancelation': {
      const msg =
        data.kind === 'booking' ? `${data.otherUserName} cancelled the booking` : `${data.otherUserName} cancelled the interview`;
      text(msg);
      break;
    }
    case 'job_shared_parent':
      text('You shared ');
      link('/seeker/jobs', 'this job');
      text(` with ${data.otherUserName}`);
      break;
    default:
      break;
  }
  return parts;
}

export default {
  name: 'MessageBubbleAdminSeeker',
  props: {
    data: {
      type: Object as PropType<SeekerAdminMessageData>,
      required: true,
    },
  },
  computed: {
    messageParts(): MessagePart[] {
      return buildMessageParts(this.data);
    },
  },
};
</script>
